import type { Attachment } from '@medplum/fhirtypes'
import type { ExtendedCoverage } from '@seen/core/models/coverage'
import { DocumentCategory } from '@seen/core/models/document-references/constants'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@seen/ui'
import { IconLink, IconUpload } from '@tabler/icons-react'
import { type JSX, useState } from 'react'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { UploadDocumentButton } from '#src/components/document-reference/UploadDocumentButton.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useLabCopySecondaryFilter } from '#src/hooks/useLabCopySecondaryFilter.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSuspenseSearch } from '#src/hooks/useSuspenseSearch.ts'

interface LinkDocumentsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  coverage: ExtendedCoverage | null
  onSuccess?: () => void
}

export function LinkDocumentsModal({
  open,
  onOpenChange,
  coverage,
  onSuccess,
}: LinkDocumentsModalProps): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('')
  const participant = useCurrentParticipant()
  const practitioner = useCurrentPractitioner()
  const createMutation = useCreateMutation<ExtendedDocumentReference>()
  const patchMutation = usePatchMutation<ExtendedDocumentReference>()

  const withoutLabCopySecondaries = useLabCopySecondaryFilter()
  const { data: allDocuments } = useSuspenseSearch(
    ExtendedDocumentReference,
    withoutLabCopySecondaries(
      ExtendedDocumentReference.byParticipant({
        participant: participant.reference,
      })
    )
  )

  const availableDocuments = allDocuments.filter((doc) => !doc.coverage)

  if (!coverage) {
    return null
  }

  const handleUpload = (attachments: Attachment[]): void => {
    const newDocRef = new ExtendedDocumentReference({
      subject: participant.reference,
      author: practitioner.reference,
      attachments,
      categories: [DocumentCategory.ENROLLMENT],
    })
    newDocRef.coverage = coverage.reference
    newDocRef.typeCode = coverage.documentType

    createMutation.mutate(newDocRef, {
      onSuccess: () => {
        onSuccess?.()
        onOpenChange(false)
      },
    })
  }

  const handleLinkExisting = (): void => {
    if (!selectedDocumentId) {
      return
    }

    const selectedDoc = availableDocuments.find(
      (doc) => doc.id === selectedDocumentId
    )
    if (!selectedDoc) {
      return
    }

    patchMutation.mutate(
      {
        instance: selectedDoc,
        mutateFn: (doc) => {
          doc.coverage = coverage.reference
          doc.typeCode = coverage.documentType
        },
      },
      {
        onSuccess: () => {
          onSuccess?.()
          onOpenChange(false)
          setSelectedDocumentId('')
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay>
        <DialogHeader className="py-4 text-lg">
          <DialogTitle>Link Documents for {coverage.typeDisplay}</DialogTitle>
          <DialogDescription>
            Upload new files or link existing files from the system
          </DialogDescription>
        </DialogHeader>
        <DialogContent className="pt-0">
          <Tabs defaultValue="upload" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload">Upload New</TabsTrigger>
              <TabsTrigger value="link">Link Existing</TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="space-y-4">
              <div className="text-center">
                <UploadDocumentButton onUpload={handleUpload}>
                  {({ onClick, onDrop, onDragOver }) => (
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto w-full flex-col gap-4 rounded-lg border-2 border-dashed border-gray-300 bg-transparent p-8 shadow-none hover:bg-transparent"
                      onClick={onClick}
                      onDrop={onDrop}
                      onDragOver={onDragOver}
                      disabled={isReadOnly}
                    >
                      <IconUpload className="size-8 text-gray-400" />
                      <span className="text-gray-600">
                        Drop files here or click to browse
                      </span>
                      <span className="inline-flex items-center rounded-md border bg-background px-4 py-2 text-sm font-medium text-foreground">
                        <IconUpload className="mr-2 size-4" />
                        Choose Files
                      </span>
                    </Button>
                  )}
                </UploadDocumentButton>
              </div>
            </TabsContent>

            <TabsContent value="link" className="space-y-4">
              <div className="space-y-4">
                <div>
                  <p className="mb-2 text-sm font-medium">
                    Select existing file to link:
                  </p>
                  <ComboboxButton
                    options={availableDocuments.map((doc) => ({
                      value: doc.id,
                      label:
                        doc.title ??
                        doc.attachments?.[0]?.title ??
                        'Untitled Document',
                    }))}
                    value={selectedDocumentId}
                    onChange={(value) => setSelectedDocumentId(value as string)}
                    placeholder="Choose a file…"
                  />
                </div>

                <div className="space-x-2 flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      onOpenChange(false)
                      setSelectedDocumentId('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleLinkExisting}
                    disabled={isReadOnly || !selectedDocumentId}
                  >
                    <IconLink className="mr-2 size-4" />
                    Link File
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </DialogDisplay>
    </Dialog>
  )
}
